import { useImperativeHandle, forwardRef, useState, ChangeEvent, useEffect, useCallback, useRef, memo } from 'react'
import styled, { css } from 'styled-components'
import { SectionTitle } from '../title/SectionTitle'
import { BtnWhiteRounded } from '../Button'
import { InputText, InputTextDiv, InputTextSection, InputTextTitle, TextWrap } from '../input/InputText'

type FocusTarget = 'name' | 'phone' | 'email' | 'password' | 'confirmPassword' | null

type OrderInfoSectionProps = {
  hasAccessToken?: boolean
  initialName?: string
  initialPhone?: string
  initialEmail?: string
}

const OrderUserInfoSection = forwardRef(
  ({ hasAccessToken = false, initialName = '', initialPhone = '', initialEmail = '' }: OrderInfoSectionProps, ref) => {
    const [editMode, setEditMode] = useState(false)
    const [name, setName] = useState(initialName)
    const [phone, setPhone] = useState(initialPhone)
    const [email, setEmail] = useState(initialEmail)
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [passwordCheck, setPasswordCheck] = useState(false)

    // 에러 상태 관리
    const [error, setError] = useState<{ [key: string]: boolean }>({})
    const [focusTarget, setFocusTarget] = useState<FocusTarget>(null)

    // ref
    const nameRef = useRef<HTMLInputElement>(null)
    const phoneRef = useRef<HTMLInputElement>(null)
    const emailRef = useRef<HTMLInputElement>(null)
    const passwordRef = useRef<HTMLInputElement>(null)
    const confirmPasswordRef = useRef<HTMLInputElement>(null)

    // 입력 핸들러
    const handleNameChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setName(value)
      setError((prev) => ({
        ...prev,
        name: value.trim() === '',
      }))
    }, [])

    const handlePhoneChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setPhone(value)
      setError((prev) => ({
        ...prev,
        phone: !/^\d{10,11}$/.test(value.replace(/-/g, '')),
      }))
    }, [])

    const handleEmailChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setEmail(value)
      setError((prev) => ({
        ...prev,
        email: !/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(value),
      }))
    }, [])

    const passwordRule = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]).{8,}$/

    const handlePasswordChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setPassword(value)
        setError((prev) => ({
          ...prev,
          password: !passwordRule.test(value),
          confirmPassword: confirmPassword !== value, // 비밀번호와 확인란이 다르면 에러, 같으면 해제
        }))
        setPasswordCheck(value !== confirmPassword || value.length === 0)
      },
      [confirmPassword, passwordRule]
    )

    const handleConfirmPasswordChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setConfirmPassword(value)
        setError((prev) => ({
          ...prev,
          confirmPassword: password !== value,
        }))
        setPasswordCheck(password !== value || password.length === 0)
      },
      [password]
    )

    const handlePhoneBlur = useCallback(() => {
      setPhone((prev) => prev.replace(/[^0-9]/g, ''))
    }, [])

    // 유효성 검사 함수
    const validate = useCallback(() => {
      if (!name) {
        setEditMode(true)
        setFocusTarget('name')
        setError((prev) => ({ ...prev, name: true }))
        alert('성명을 입력해 주세요.')
        return false
      }
      if (!phone || !/^\d{10,11}$/.test(phone.replace(/-/g, ''))) {
        setEditMode(true)
        setFocusTarget('phone')
        setError((prev) => ({ ...prev, phone: true }))
        alert('휴대폰 번호를 정확히 입력해 주세요.')
        return false
      }
      if (!email || !/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(email)) {
        setEditMode(true)
        setFocusTarget('email')
        setError((prev) => ({ ...prev, email: true }))
        alert('이메일을 정확히 입력해 주세요.')
        return false
      }
      if (!hasAccessToken) {
        if (!password || !passwordRule.test(password)) {
          setEditMode(true)
          setFocusTarget('password')
          setError((prev) => ({ ...prev, password: true }))
          alert('비밀번호는 영문, 숫자, 특수문자 조합 8자 이상이어야 합니다.')
          return false
        }
        if (password !== confirmPassword) {
          setEditMode(true)
          setFocusTarget('confirmPassword')
          setError((prev) => ({ ...prev, confirmPassword: true }))
          alert('비밀번호가 일치하지 않습니다.')
          return false
        }
      }
      return true
    }, [name, phone, email, hasAccessToken, password, confirmPassword])

    const handleEditButtonClick = () => {
      if (editMode) {
        if (validate()) setEditMode(false)
      } else {
        setEditMode(true)
      }
    }

    useEffect(() => {
      setName(initialName)
    }, [initialName])

    useEffect(() => {
      setPhone(initialPhone)
    }, [initialPhone])

    useEffect(() => {
      setEmail(initialEmail)
    }, [initialEmail])

    // 포커스 설정
    useEffect(() => {
      if (editMode && focusTarget) {
        if (focusTarget === 'name') nameRef.current?.focus()
        if (focusTarget === 'phone') phoneRef.current?.focus()
        if (focusTarget === 'email') emailRef.current?.focus()
        if (focusTarget === 'password') passwordRef.current?.focus()
        if (focusTarget === 'confirmPassword') confirmPasswordRef.current?.focus()
        setFocusTarget(null)
      }
    }, [editMode, focusTarget])

    // 초기화
    useEffect(() => {
      if (hasAccessToken) {
        setEditMode(false)
        setPassword('')
        setConfirmPassword('')
        setError({})
      } else {
        setEditMode(true)
      }
    }, [hasAccessToken])

    useImperativeHandle(
      ref,
      () => ({
        validate,
        getValues: () => ({
          name,
          phone,
          email,
          password,
          confirmPassword,
        }),
      }),
      [name, phone, email, password, confirmPassword, validate]
    )

    return (
      <>
        <Section>
          <FlexSection $hasAccessToken={hasAccessToken}>
            <SectionTitle>주문자 정보</SectionTitle>
            {hasAccessToken && (
              <BtnWhiteRounded style={{ marginLeft: 'auto' }} onClick={handleEditButtonClick}>
                {editMode ? '수정 완료' : '정보 수정'}
              </BtnWhiteRounded>
            )}
          </FlexSection>
          <StyledForm autoComplete='off' onSubmit={(e) => e.preventDefault()}>
            <InputTextSection>
              <InputTextDiv>
                <InputTextTitle>주문자</InputTextTitle>
                {editMode ? (
                  <StyledInputText
                    ref={nameRef}
                    name='ordererName'
                    autoComplete='name'
                    placeholder='성명을 입력해주세요.'
                    value={name}
                    onChange={handleNameChange}
                    $error={error.name}
                  />
                ) : (
                  <span>{name || '-'}</span>
                )}
              </InputTextDiv>
              <InputTextDiv>
                <InputTextTitle>휴대폰 번호</InputTextTitle>
                {editMode ? (
                  <StyledInputText
                    ref={phoneRef}
                    name='ordererPhone'
                    autoComplete='tel'
                    placeholder='‘-’을 제외하고 입력해주세요.'
                    value={phone.replaceAll('-', '')}
                    onChange={handlePhoneChange}
                    onBlur={handlePhoneBlur}
                    $error={error.phone}
                  />
                ) : (
                  <span>{phone || '-'}</span>
                )}
              </InputTextDiv>
              <InputTextDiv>
                <InputTextTitle>이메일</InputTextTitle>
                {editMode ? (
                  <StyledInputText
                    ref={emailRef}
                    name='ordererEmail'
                    autoComplete='email'
                    placeholder='이메일을 입력해주세요.'
                    value={email}
                    onChange={handleEmailChange}
                    $error={error.email}
                  />
                ) : (
                  <span>{email || '-'}</span>
                )}
              </InputTextDiv>
              {!hasAccessToken && (
                <>
                  <InputTextDiv>
                    <InputTextTitle>비밀번호</InputTextTitle>
                    <StyledInputText
                      ref={passwordRef}
                      type='password'
                      name='ordererPassword'
                      autoComplete='new-password'
                      placeholder='비밀번호를 입력해주세요.'
                      value={password}
                      onChange={handlePasswordChange}
                      $error={error.password}
                    />
                  </InputTextDiv>
                  <InputTextDiv>
                    <InputTextTitle>비밀번호 확인</InputTextTitle>
                    <TextWrap>
                      <StyledInputText
                        ref={confirmPasswordRef}
                        type='password'
                        name='ordererPasswordConfirm'
                        autoComplete='new-password'
                        placeholder='비밀번호를 다시 입력해주세요.'
                        value={confirmPassword}
                        onChange={handleConfirmPasswordChange}
                        $error={error.confirmPassword}
                      />
                    </TextWrap>
                  </InputTextDiv>
                  <InputTextDiv>
                    <InputTextTitle></InputTextTitle>
                    <p
                      style={{
                        color: passwordCheck ? 'red' : '#898989',
                        fontSize: 12,
                        fontWeight: 500,
                        lineHeight: '16px',
                      }}
                    >
                      {passwordCheck && password
                        ? '비밀번호를 다시 확인 해주세요.'
                        : '영문, 숫자, 특수문자 조합 8자 이상'}
                    </p>
                  </InputTextDiv>
                </>
              )}
            </InputTextSection>
          </StyledForm>
        </Section>
      </>
    )
  }
)

const StyledForm = styled.form`
  width: 100%;
  display: flex !important;
  flex-direction: column;
`

const StyledInputText = styled(InputText)<{ $error?: boolean }>`
  ${({ $error }) =>
    $error &&
    css`
      border-color: red !important;
      outline: none;
    `}
`

const FlexSection = styled.div<{ $hasAccessToken?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: ${({ $hasAccessToken }) => ($hasAccessToken ? '20px' : '0')};
  .flex-section-inner {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
  }
`

const Section = styled.div`
  padding: 24px 20px;
  @media screen and (min-width: 768px) {
    padding: 32px 0 32px 0;
    border-bottom: 1px solid var(--colGray3);
    &:last-child {
      border-bottom: none;
    }
  }
`

OrderUserInfoSection.displayName = 'OrderUserInfoSection'
export default memo(OrderUserInfoSection)
